import { ApolloQueryResult } from '@apollo/client';
import { t } from 'i18next';
import { useQuery } from '@tanstack/react-query';
import { executeOperationSafely } from '@stigg-common';
import {
  ActiveSubscriptionsFragment,
  GetActiveSubscriptionsQuery,
  GetActiveSubscriptionsQueryVariables,
} from '@stigg-types/apiTypes';
import { RootState } from '../../../redux/store';
import { apolloClient } from '../../../ApolloClient';
import { useDispatch, useSelector } from 'react-redux';
import { GET_ACTIVE_SUBSCRIPTIONS } from './fetchActiveSubscriptions';

type UseGetActiveSubscriptionsProps = {
  customerId: string;
  resourceId: string | undefined;
};

export const useGetActiveSubscriptions = ({ customerId, resourceId }: UseGetActiveSubscriptionsProps) => {
  const dispatch = useDispatch();
  const environmentId = useSelector((state: RootState) => state.accountReducer.currentEnvironmentId) as string;

  return useQuery({
    queryKey: ['activeSubscriptions', customerId, resourceId],
    queryFn: () =>
      executeOperationSafely(
        async () =>
          apolloClient.query<GetActiveSubscriptionsQuery, GetActiveSubscriptionsQueryVariables>({
            query: GET_ACTIVE_SUBSCRIPTIONS,
            fetchPolicy: 'network-only',
            variables: {
              input: {
                customerId,
                resourceId,
                environmentId: environmentId || '',
              },
            },
          }),
        { failureMessageHandler: () => t('customers.api.failSubscriptionFetch') },
        dispatch,
      ),
    select: (result: ApolloQueryResult<GetActiveSubscriptionsQuery>) =>
      result.data?.getActiveSubscriptions as ActiveSubscriptionsFragment[],
    retry: 0, // we automatically retry in apollo client
  });
};
